import { pick, isEmpty } from 'mojito/utils';
import EventCardsTypes from 'modules/event-cards/types.js';

/**
 * Utility functions associated with the event cards carousel module.
 *
 * @class EventCardsCarouselUtils
 * @name utils
 * @memberof Mojito.Modules.EventCardsCarousel
 */
export default class EventCardsCarouselUtils {
    /**
     * Converts promotion object into a card.
     *
     * @param {Mojito.Services.Promotions.types.Promotion} promotion - Promotion that should be represented as a card.
     *
     * @returns {Mojito.Modules.EventCards.types.Cards} Promotion card.
     * @function Mojito.Modules.EventCardsCarousel.convertToCard
     */
    static convertToCard(promotion) {
        const resolveCard = resolveCardConverter(promotion);
        return {
            ...pick(promotion, 'id', 'displayOrder', 'altText'),
            ...resolveCard(promotion),
        };
    }

    /**
     * Enrich with fallback cards if total amount of provided cards is smaller than minCardsNumber.
     * This method does not mutate cards list. The fallback cards will be added to in the end of a list.
     *
     * @param {Array<Mojito.Modules.EventCards.types.Card>} cards - Cards to enrich.
     * @param {number} minCardsNumber - Minimum required cards number. This property is used to make a decision if fallback cards should be added and how many of them.
     * @param {Array<Mojito.Modules.EventCards.types.Card>} fallbackCards - Fallback cards to be used.
     *
     * @returns {Array<Mojito.Modules.EventCards.types.Card>} Cards with a fallback cards or original cards list if not fallback cards were added.
     * @function Mojito.Modules.EventCardsCarousel.enrichWithFallbackCards
     */
    static enrichWithFallbackCards(cards, minCardsNumber, fallbackCards) {
        if (isEmpty(cards)) {
            return cards;
        }
        const missingCardsNumber = minCardsNumber - cards.length;
        const fallbackCardsNumber = Math.min(missingCardsNumber, fallbackCards.length);
        return fallbackCardsNumber > 0
            ? [...cards, ...fallbackCards.slice(0, fallbackCardsNumber)]
            : cards;
    }
}

const isPropCard = promotion =>
    promotion.cardType?.toUpperCase().includes(EventCardsTypes.CARD_TYPE.PROP);

const resolveCardConverter = promotion => {
    if (isPropCard(promotion)) {
        return resolvePropCard;
    }
    // An image on an event promotion is a card background, not a banner. Only a promotion that
    // carries no event has nothing to render but the image itself.
    if (promotion.imgUrl && !promotion.event) {
        return resolveBannerCard;
    }
    return resolveEventCard;
};

const resolvePropCard = promotion => {
    const { event, displayOrder, text } = promotion;
    const selection = event?.market?.selection;

    return event
        ? {
              type: EventCardsTypes.CARD_TYPE.PROP,
              displayOrder,
              sportId: event.sportId,
              content: {
                  eventId: event.id,
                  marketId: event.market?.marketId,
                  selectionId: selection?.selectionId,
                  imageUrl: promotion.imgUrl,
                  text,
                  ...pick(promotion, 'backgroundColor', 'backgroundImage', 'imageFilter'),
                  ...pick(event, 'gameLine', 'marketLines'),
              },
          }
        : {};
};

const resolveEventCard = promotion => {
    const { event, displayOrder, text, imgUrl, cardBannerDetails } = promotion;
    return event
        ? {
              type: event.market?.selection ? EventCardsTypes.CARD_TYPE.SELECTION : event.eventType,
              displayOrder: displayOrder,
              sportId: event.sportId,
              content: {
                  eventId: event.id,
                  marketId: event.market?.marketId,
                  boostedMarketId: event?.boostedMarketId,
                  marketName: event.market?.marketName,
                  text,
                  imgUrl,
                  colorMode: imgUrl
                      ? resolveEventCardColorMode(cardBannerDetails?.colorMode)
                      : undefined,
                  selectionId: event.market?.selection?.selectionId,
                  selectionName: event.market?.selection?.selectionName,
                  ...pick(event, 'gameLine', 'marketLines'),
              },
          }
        : {};
};

const resolveEventCardColorMode = colorMode => {
    const colorModes = EventCardsTypes.COLOR_MODES;
    return {
        [colorModes.LIGHT]: colorModes.DARK,
        [colorModes.DARK]: colorModes.LIGHT,
    }[colorMode];
};

const resolveBannerCard = promotion => {
    const { imgUrl, target, displayOrder, cardBannerDetails, openInNewTab } = promotion;
    return {
        type: EventCardsTypes.CARD_TYPE.BANNER,
        displayOrder: displayOrder,
        content: {
            imgUrl,
            cardBannerDetails,
            openInNewTab,
            ...pick(target, 'type', 'url', 'altText'),
        },
    };
};
